import React, { useState } from 'react';
import { addDoc, collection, updateDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import { Brain, Loader, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Goal, Game } from '../types';
import { analyzeGamesWithAI } from '../services/aiTrainerService';
import { incrementUsage } from '../services/usageService';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

interface AITrainerButtonProps {
  gameIds: string[];
  games?: Game[];
  goals?: Goal[];
  className?: string;
  variant?: 'primary' | 'secondary';
}

export default function AITrainerButton({
  gameIds,
  games = [],
  goals = [],
  className = '',
  variant = 'primary',
}: AITrainerButtonProps) {
  const user = useStore((state) => state.user);
  const profile = useStore((state) => state.profile);
  const profileLoading = useStore((state) => state.profileLoading);
  const getUsageInfo = useStore((state) => state.getUsageInfo);
  const syncProfileCounts = useStore((state) => state.syncProfileCounts);
  const language = useStore((state) => state.language);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { createRipple } = useRipple();

  // Get usage info from centralized store
  const usageInfo = getUsageInfo('analysis');

  const handleAnalyze = async (event: React.MouseEvent<HTMLButtonElement>) => {
    createRipple(event);

    if (!user || gameIds.length === 0) return;

    setLoading(true);
    setError('');

    // Force a fresh check by syncing profile counts before analysis
    try {
      await syncProfileCounts(user.uid);
    } catch (error) {
      console.error('Error syncing profile before analysis:', error);
    }

    // Get fresh usage info after sync
    const freshUsageInfo = getUsageInfo('analysis');

    // Check usage limits with fresh data
    if (freshUsageInfo && !freshUsageInfo.allowed) {
      setError(
        t('aiTrainer.upgradeRequired')
      );
      setLoading(false);
      return;
    }

    try {
      // Create a new AI trainer session
      const sessionData = {
        userId: user.uid,
        gameIds,
        title:
          games.length === 1
            ? `${t('aiTrainer.title')}: vs ${games[0].opponent}`
            : `${t('aiTrainer.title')}: ${games.length} Games`,
        status: 'pending',
        analysis: '',
        drills: [],
        tips: [],
        motivationalNote: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const sessionDoc = await addDoc(
        collection(db, 'ai_trainer_sessions'),
        sessionData
      );

      // Generate AI analysis using profile data from store
      const aiResult = await analyzeGamesWithAI(games, goals, profile, language);

      // Update the session with the analysis
      await updateDoc(doc(db, 'ai_trainer_sessions', sessionDoc.id), {
        analysis: aiResult.analysis,
        drills: aiResult.drills,
        tips: aiResult.tips,
        motivationalNote: aiResult.motivationalNote,
        status: 'completed',
        updatedAt: new Date(),
      });

      // Increment usage count after successful analysis
      if (!freshUsageInfo?.hasPaid) {
        await incrementUsage(user.uid, 'analysis');
      }

      // Navigate to the session page
      navigate(`/ai-trainer/${sessionDoc.id}`);
    } catch (err) {
      console.error('Error creating AI trainer session:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to create analysis session'
      );
    } finally {
      setLoading(false);
    }
  };

  const variantClasses = {
    primary: 'btn-mui-primary btn-ripple',
    secondary: 'btn-mui-secondary btn-ripple',
  };

  // Show loading state while profile is loading
  if (profileLoading) {
    return (
      <button disabled className={`btn-mui-primary btn-ripple ${className}`}>
        <Loader className='h-5 w-5 animate-spin' />
        <span>{t('common.loading')}</span>
      </button>
    );
  }

  return (
    <div>
      <div className='flex items-center gap-3'>
        <button
          onClick={handleAnalyze}
          disabled={loading || !user || gameIds.length === 0}
          className={`${variantClasses[variant]} ${className}`}
        >
          {loading ? (
            <Loader className='h-5 w-5 animate-spin' />
          ) : (
            <Brain className='h-5 w-5' />
          )}
          <span>{loading ? t('aiTrainer.analyzing') : t('aiTrainer.analyzeWithAI')}</span>
        </button>

        {usageInfo && !usageInfo.hasPaid && usageInfo.usage && (
          <div className='text-sm text-gray-600'>
            {t('aiTrainer.title')}: {usageInfo.usage.used}/{usageInfo.usage.limit}
          </div>
        )}
      </div>

      {error && (
        <div className='mt-2 flex items-start'>
          <AlertCircle className='h-4 w-4 text-red-500 mr-1 mt-0.5 flex-shrink-0' />
          <p className='text-sm text-red-600'>{error}</p>
        </div>
      )}

      {usageInfo && !usageInfo.allowed && (
        <div className='text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-md border border-red-200'>
          <div className='flex items-center'>
            <AlertCircle className='h-4 w-4 mr-2 flex-shrink-0' />
            <div>
              <p className='font-medium'>{t('aiTrainer.limitReached')}</p>
              <p className='text-sm'>
                {t('aiTrainer.limitMessage', {
                  used: usageInfo.usage?.used?.toString() || '0',
                  limit: usageInfo.usage?.limit?.toString() || '0'
                })}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
